import { flow, getSnapshot, IStateTreeNode, types } from 'mobx-state-tree'
import { DOMAIN_ACTION } from '@/constants'
import { IValueChange } from '@/pages/supplier/listing/definition/accountDetails'
import { axiosPrivateHelper } from '@/utils/axiosHelper'
const axios = axiosPrivateHelper()

const ErrorValidateModel = types.model('ErrorValidateModel').props({
  error1: types.maybe(types.boolean),
  error2: types.maybe(types.boolean),
  errorLabel1: types.maybeNull(types.string),
  errorLabel2: types.maybeNull(types.string),
})

const ValueModel = types.model('ValueModel').props({
  isSpecific: types.maybeNull(types.boolean),
  value1: types.maybeNull(types.string),
  value2: types.maybeNull(types.string),
})

const QuestionModel = types.model('QuestionModel').props({
  component: types.maybeNull(types.string),
  id: types.string,
  label: types.maybeNull(types.string),
  value: types.maybeNull(ValueModel),
  errorValidate: types.maybeNull(types.optional(ErrorValidateModel, {} as any)),
})

const LocationModel = types.model('LocationModel').props({
  id: types.optional(types.maybe(types.number), 0),
  name: types.maybeNull(types.string),
  country_id: types.maybeNull(types.number),
})

export const ListingDetailModel = types
  .model('ListingDetailModel')
  .props({
    website_url: types.maybeNull(types.string),
    custom_tracking_parameters: types.maybeNull(types.boolean),
    listing_name: types.maybeNull(types.string),
    _prev_listing_name: types.maybeNull(types.string),
    contact_number: types.maybeNull(types.string),
    contact_email: types.maybeNull(types.string),
    locations: types.optional(types.array(LocationModel), []),
    business_description: types.maybeNull(types.string),
    listing_summary: types.maybeNull(types.string),
    dependent_question: types.maybeNull(
      types.optional(types.array(QuestionModel), [])
    ),
    listing_setup_step: types.optional(types.number, 1),
    business_category: types.maybeNull(types.number),
    status: types.optional(
      types.enumeration('status', ['changed', 'keep']),
      'keep'
    ),
  })
  .actions((self: any | IStateTreeNode) => {
    const setStatus = (value: 'changed' | 'keep') => {
      self.status = value
    }

    const mapValueChanged = ({ add, value }: IValueChange) => {
      switch (add) {
        case 'website_url':
          self.website_url = value
          break
        case 'custom_tracking_parameters':
          self.custom_tracking_parameters = value
          break
        case 'listing_name':
          if (self.listing_name === null) {
            self._prev_listing_name = value
          }

          self.listing_name = value
          break
        case 'business_contact_number':
          self.contact_number = value
          break
        case 'business_email':
          self.contact_email = value
          break
        case 'locations':
          self.locations = value
          break
        case 'business_description':
          self.business_description = value
          break
        case 'dependent_question':
          self.dependent_question = value
          break
        case 'listing_summary':
          self.listing_summary = value
          break
        case 'business_category':
          self.business_category = value
          break
        default:
          null
          break
      }

      setStatus('changed')
    }

    const isCompletedform = (data: any, navigation: any) => {
      const isBusinessName = self.listing_name && self.listing_name.length > 0
      const isListingSummary =
        self.listing_summary && self.listing_summary.length > 0
      const isBusinessDescription =
        self.business_description && self.business_description.length > 0
      const listingDetailNav = {
        ...navigation.find((item: any) => item.value === 'listing-details'),
        completed: isBusinessName && isListingSummary && isBusinessDescription,
      }
      navigation[
        navigation.findIndex((item: any) => item.value === 'listing-details')
      ] = listingDetailNav
      return navigation
    }

    const update = flow(function* (id: string, data: any = null) {
      const URI = `${DOMAIN_ACTION.API.UPDATE_SUPPLIER_LISTING(id)}`
      const payload: any = {
        website_url: self.website_url,
        custom_tracking_parameters: self.custom_tracking_parameters,
        contact_number: self.contact_number,
        contact_email: self.contact_email,
        locations: getSnapshot(self.locations),
        business_description: self.business_description,
        listing_summary: self.listing_summary,
        dependent_question: getSnapshot(self.dependent_question),
        business_category_id: self.business_category,
        listing_setup_step: 1,
      }

      if (
        self.listing_name !== null &&
        self._prev_listing_name !== self.listing_name
      ) {
        payload.listing_name = self.listing_name
      }

      const res = yield axios.put(URI, data || payload)
      self._prev_listing_name = self.listing_name
      return res
    })

    return { setStatus, mapValueChanged, update, isCompletedform }
  })
